import type { ContentIndex } from '@ezishizi/content-schema';
import { nodeDone, type PathSection } from './path.ts';
import { activityKey, type ProgressMap } from './progress.ts';

/**
 * Story films. Every three P1 lessons a film unlocks: film k plays the stories of lessons
 * 3k-2 to 3k back to back, and becomes available once lesson 3k is complete (every stop of it
 * done, the same test that moves the child on to the next lesson).
 */
export const FILM_LEVEL = 'p1';
export const FILM_SPAN = 3;
export const FILM_COUNT = 6;

export interface FilmStory {
  lessonId: string;
  storyId: string;
}

export interface Film {
  /** 1-based; the route param. */
  n: number;
  /** The lesson whose completion unlocks it (and whose id the watch session is recorded on). */
  unlockLessonId: string;
  unlockOrder: number;
  /** The lessons whose stories it plays, in order. */
  lessonIds: string[];
  /** Their stories, a lesson without one skipped. */
  stories: FilmStory[];
}

/** The film a lesson unlocks when it is completed, if any. */
export function filmNumberForLesson(order: number): number | null {
  return order % FILM_SPAN === 0 && order / FILM_SPAN <= FILM_COUNT ? order / FILM_SPAN : null;
}

/** The films of a level (none for any level but P1), built from the index alone. */
export function buildFilms(index: ContentIndex, levelId: string): Film[] {
  if (levelId !== FILM_LEVEL) return [];
  const level = index.levels.find((l) => l.id === levelId);
  if (!level) return [];
  const lessons = level.lessonIds
    .map((id) => index.lessons.find((l) => l.id === id))
    .filter((l): l is NonNullable<typeof l> => !!l)
    .sort((a, b) => a.order - b.order);
  const films: Film[] = [];
  for (let n = 1; n <= FILM_COUNT; n++) {
    const unlock = lessons.find((l) => l.order === n * FILM_SPAN);
    if (!unlock) continue;
    const span = lessons.filter((l) => l.order > (n - 1) * FILM_SPAN && l.order <= n * FILM_SPAN);
    films.push({
      n,
      unlockLessonId: unlock.id,
      unlockOrder: unlock.order,
      lessonIds: span.map((l) => l.id),
      stories: span.flatMap((l) =>
        l.storyIds[0] ? [{ lessonId: l.id, storyId: l.storyIds[0] }] : [],
      ),
    });
  }
  return films;
}

export function filmByNumber(films: Film[], n: number): Film | null {
  return films.find((f) => f.n === n) ?? null;
}

/** Whether the unlocking lesson's every stop is done. A lesson not on the path is not done. */
export function filmUnlocked(film: Film, sections: PathSection[], progress: ProgressMap): boolean {
  const section = sections.find((s) => s.lesson.id === film.unlockLessonId);
  return !!section && film.stories.length > 0 && section.nodes.every((n) => nodeDone(n, progress));
}

export const filmKey = (film: Film): string => activityKey(film.unlockLessonId, null, 'film');

export function filmWatched(film: Film, progress: ProgressMap): boolean {
  return progress.has(filmKey(film));
}

// Which unlocks the child has been shown the "film unlocked" celebration for, remembered per
// profile on this device. Losing it only means the celebration plays once more.
const seenKey = (profileId: string) => `ezishizi:films-seen:${profileId}`;

export function seenFilms(profileId: string): Set<number> {
  try {
    const raw = JSON.parse(localStorage.getItem(seenKey(profileId)) ?? '[]') as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((x): x is number => typeof x === 'number') : []);
  } catch {
    return new Set();
  }
}

export function markFilmSeen(profileId: string, n: number): void {
  try {
    localStorage.setItem(seenKey(profileId), JSON.stringify([...seenFilms(profileId).add(n)]));
  } catch {
    /* private mode: it may celebrate again */
  }
}

/** The first unlocked film the child has not been told about, if any. */
export function filmToCelebrate(
  films: Film[],
  sections: PathSection[],
  progress: ProgressMap,
  seen: Set<number>,
): Film | null {
  return films.find((f) => !seen.has(f.n) && filmUnlocked(f, sections, progress)) ?? null;
}

// ---- the timeline ----

export type FilmBeat =
  | { kind: 'title'; storyId: string; lessonId: string; part: number; of: number }
  | { kind: 'scene'; storyId: string; lessonId: string; sceneIndex: number; sceneId: string }
  | { kind: 'end' };

/**
 * The beats of a film, given the scenes of each of its stories: a title card, the story's
 * scenes, and after the last story one ending card. A story that failed to load (`null`) or has
 * no scenes is skipped, and a film with nothing left to show has no beats at all.
 */
export function filmBeats(
  stories: Array<{ lessonId: string; storyId: string; sceneIds: string[] } | null>,
): FilmBeat[] {
  const real = stories.filter((s): s is NonNullable<typeof s> => !!s && s.sceneIds.length > 0);
  const out: FilmBeat[] = [];
  real.forEach((s, i) => {
    out.push({
      kind: 'title',
      storyId: s.storyId,
      lessonId: s.lessonId,
      part: i + 1,
      of: real.length,
    });
    s.sceneIds.forEach((sceneId, sceneIndex) =>
      out.push({ kind: 'scene', storyId: s.storyId, lessonId: s.lessonId, sceneIndex, sceneId }),
    );
  });
  if (real.length) out.push({ kind: 'end' });
  return out;
}

/** Shortest a scene stays up, so a short (or silent) clip still gets read. */
export const MIN_SCENE_MS = 3500;
export const TITLE_MS = 2600;
export const END_MS = 4500;

/** A slow push in or a drift across, alternating so back-to-back scenes do not all move alike. */
export interface CameraMove {
  from: { scale: number; x: number; y: number };
  to: { scale: number; x: number; y: number };
}

export function cameraFor(sceneIndex: number): CameraMove {
  switch (sceneIndex % 4) {
    case 0:
      return { from: { scale: 1, x: 0, y: 0 }, to: { scale: 1.12, x: -1.5, y: -1 } };
    case 1:
      return { from: { scale: 1.1, x: -2, y: 0 }, to: { scale: 1.1, x: 2, y: 0 } };
    case 2:
      return { from: { scale: 1.12, x: 1.5, y: -1 }, to: { scale: 1, x: 0, y: 0 } };
    default:
      return { from: { scale: 1.1, x: 2, y: 1 }, to: { scale: 1.1, x: -2, y: 1 } };
  }
}

/** How an actor enters: the mascot slides in from the side it stands on, props drop or rise. */
export type EnterMotion = 'slide-left' | 'slide-right' | 'drop' | 'rise';

export function enterMotion(x: number, kind: 'mascot' | 'word', order: number): EnterMotion {
  if (kind === 'mascot') return x < 50 ? 'slide-left' : 'slide-right';
  return order % 2 === 0 ? 'drop' : 'rise';
}
